import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Everything the workflows section draws in place of a list.
 *
 * Split from `WorkflowsSection` to keep that file about the section's shape
 * rather than its five outcomes; every one of these renders inside its shell.
 */
export function WorkflowCardSkeleton() {
  return (
    <Card className="gap-2 px-5 py-4">
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-3 w-1/4" />
      <Skeleton className="h-3 w-3/4" />
    </Card>
  )
}

// The states below sit inside a page section rather than filling one, so they
// keep the wording of the standalone page at a smaller vertical scale.
//
// One block for every retry this section can offer — the list's own failure,
// the degraded catalogue and the boundary's fallback — so their spacing and
// their button cannot drift apart on the same page.
export function ErrorState({
  message = 'Couldn’t load workflows. Please try again.',
  label = 'Retry',
  detail,
  onRetry,
}: {
  message?: string
  label?: string
  detail?: ReactNode
  onRetry: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <p className="text-muted-foreground">{message}</p>
      {detail}
      <Button size="sm" variant="outline" onClick={onRetry}>
        {label}
      </Button>
    </div>
  )
}

export function EmptyState() {
  return (
    <div className="py-10 text-center text-muted-foreground">
      No workflows defined for this organization yet.
    </div>
  )
}

// Nebula unreachable or unconfigured. Named for the cause rather than styled as
// an error: an environment with no Nebula is supported, so this is the normal
// appearance of the section there, not a fault to escalate. Retry stays because
// the outage case is genuinely transient.
export function CatalogueUnavailableState({
  onRetry,
}: {
  onRetry: () => void
}) {
  return (
    <ErrorState
      message="The workflow catalogue is unavailable, so this list may be incomplete."
      detail={
        <p className="max-w-md text-sm text-muted-foreground">
          Workflow definitions come from Nebula. This organization may still
          have workflows — they just can’t be listed right now.
        </p>
      }
      onRetry={onRetry}
    />
  )
}

// Reachable for a Careforce global admin, who is admitted by their memberships
// rather than by having an org selected.
export function NoOrgState() {
  return (
    <div className="py-10 text-center text-muted-foreground">
      Select an organization to see its workflows.
    </div>
  )
}
